import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Row,
  Col,
  Button,
  Offcanvas,
  Badge,
  ListGroup,
  Card,
  Form,
  Spinner,
  Alert,
  Modal,
} from "react-bootstrap";
import "../Accounts/acc-list.css";
import ICD10PickerModal from "../SearchICD10/SearchICD10";
import {
  getPatientDiagnoses,
  createPatientDiagnosis,
  updatePatientDiagnosis,
  deletePatientDiagnosis,
} from "../../api";
import "../SearchICD10/SearchICD10.css";

const codeKeyOf = (obj) => obj?.ICD10_Code || obj?.ICD10_3_Code || "";
const icdLabel = (icd) => {
  if (!icd) return "";
  const code = icd.ICD10_Code || icd.ICD10_3_Code || "";
  const desc = icd.ICD10_Description || icd.ICD10_3_Description || "";
  return `${code} — ${desc}`.trim();
};

const normalize = (d) => ({
  _id: d?._id || undefined,
  date: (d?.date || "").slice(0, 10),
  note: d?.note || "",
  icds: Array.isArray(d?.icds)
    ? d.icds.map((s, i) => ({
        uid: `${codeKeyOf(s.item ?? s)}-${i}-${Math.random().toString(36).substr(2, 5)}`,
        item: s.item ?? s,
        isPrimary: !!s.isPrimary,
        useOnInvoice: !!s.useOnInvoice,
        isPMB: !!s.isPMB,
        pmbAuthNo: s.pmbAuthNo || "",
        pmbStartDate: s.pmbStartDate || "",
        pmbEndDate: s.pmbEndDate || "",
      }))
    : [],
  createdAt: d?.createdAt,
  updatedAt: d?.updatedAt,
});

const sortNewest = (a, b) => {
  const at = new Date(a.date || a.createdAt || 0).getTime();
  const bt = new Date(b.date || b.createdAt || 0).getTime();
  return bt - at || String(b._id || "").localeCompare(String(a._id || ""));
};

// PMB Auth Modal Component
const PMBAuthModal = ({ show, onHide, onSave, authNo, startDate }) => {
  const [localAuthNo, setLocalAuthNo] = useState(authNo || "");
  const [localStartDate, setLocalStartDate] = useState(startDate || "");
  const [localEndDate, setLocalEndDate] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (show) {
      setLocalAuthNo(authNo || "");
      setLocalStartDate(startDate || "");
      setError("");
    }
  }, [show, authNo, startDate]);

  const handleSave = () => {
    if (!localAuthNo.trim()) {
      setError("Authorization Number is required");
      return;
    }
    onSave(localAuthNo, localStartDate, localEndDate);
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header
              closeButton
              style={{ background: "#CFE2FF" }}
              className="rounded-0 text-primary d-flex align-items-center"
            > <Modal.Title as="h6" className="m-0">PMB Authorization Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
        
        <Form.Group className="mb-3">
          <Form.Label className="fw-semibold">
            Authorization Number <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            type="text"
            value={localAuthNo}
            onChange={(e) => {
              setLocalAuthNo(e.target.value);
              setError("");
            }}
            placeholder="Enter auth number"
            autoFocus
          />
        </Form.Group>

        <Form.Group>
          <Form.Label className="fw-semibold">Start Date (Optional)</Form.Label>
          <Form.Control
            type="date"
            value={localStartDate}
            onChange={(e) => setLocalStartDate(e.target.value)}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label className="fw-semibold">Start Date (Optional)</Form.Label>
          <Form.Control
            type="date"
            value={localEndDate}
            onChange={(e) => setLocalEndDate(e.target.value)}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const ICDEditRow = React.memo(({ icd, index, stableKey, label, onUpdate, onEditPMB }) => {
  return (
    <div className="border rounded p-2 mb-2 bg-light-subtle">
      <div className="fw-semibold text-primary small mb-1">
        {label || "(ICD code)"}
      </div>

      <Row className="align-items-center g-2">
        <Col xs={12} sm={4}>
          <Form.Check
            type="checkbox"
            id={`useInvoice-${stableKey}`}
            label="Use on Invoice"
            checked={!!icd.useOnInvoice}
            onChange={(e) => {
              onUpdate(index, {
                ...icd,
                useOnInvoice: e.target.checked,
                ...(e.target.checked ? {} : { isPMB: false, pmbAuthNo: "", pmbStartDate: "" }),
              });
            }}
          />
        </Col>

        <Col xs={12} sm={3}>
          <Form.Check
            type="checkbox"
            id={`isPMB-${stableKey}`}
            label="PMB"
            checked={!!icd.isPMB}
            disabled={!icd.useOnInvoice}
            onChange={(e) => {
              const isChecked = e.target.checked;
              if (isChecked) {
                // Open modal when PMB is checked
                onEditPMB(index);
              } else {
                // Clear PMB data when unchecked
                onUpdate(index, {
                  ...icd,
                  isPMB: false,
                  pmbAuthNo: "",
                  pmbStartDate: "",
                  pmbEndDate: "",
                });
              }
            }}
          />
        </Col>

        <Col xs={12} sm={5}>
          {icd.isPMB && icd.pmbAuthNo && (
            <div className="d-flex align-items-center gap-2">
              <div className="small text-muted flex-grow-1">
                <div><strong>Auth:</strong> {icd.pmbAuthNo}</div>
                {icd.pmbStartDate && (
                  <div><strong>Start:</strong> {icd.pmbStartDate} {" "}
                  <strong>End:</strong> {icd.pmbEndDate}</div>
                )}
              </div>
              <Button
                size="sm"
                variant=""
                className="fs-6 text-primary"
                onClick={() => onEditPMB(index)}
                title="Edit PMB details"
              >
                <i className="fa-solid fa-pen-to-square" />
              </Button>
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
}, (prev, next) =>
    prev.index === next.index &&
    prev.stableKey === next.stableKey &&
    prev.icd === next.icd
);

// practiceId scopes the list and stamps new diagnoses; see
// resolveDiagnosisPractice in sessionsRoutes.js for the fallback when absent.
export default function DiagnosisTwoPane({ patientId, practiceId }) {
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showPicker, setShowPicker] = useState(false);
  const [pickerOpenFor, setPickerOpenFor] = useState(null);

  const [isEditing, setIsEditing] = useState(false);
  const [editDraft, setEditDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [isEditingICDs, setIsEditingICDs] = useState(false);
  const [hasEditedICDs, setHasEditedICDs] = useState(false);

  // PMB Modal state
  const [showPMBModal, setShowPMBModal] = useState(false);
  const [pmbEditIndex, setPMBEditIndex] = useState(null);

  const NEW_ID = "__new__";

  const handleICDUpdate = useCallback((index, updatedIcd) => {
    setEditDraft((prev) => {
      if (!prev || !prev.icds) return prev;
      const newIcds = [...prev.icds];
      newIcds[index] = updatedIcd;
      return {
        ...prev,
        icds: newIcds,
      };
    });
  }, []);

  const handleEditPMB = useCallback((index) => {
    setPMBEditIndex(index);
    setShowPMBModal(true);
  }, []);

  const handleSavePMB = useCallback((authNo, startDate, endDate) => {
    if (pmbEditIndex === null) return;
    
    setEditDraft((prev) => {
      if (!prev || !prev.icds) return prev;
      const newIcds = [...prev.icds];
      newIcds[pmbEditIndex] = {
        ...newIcds[pmbEditIndex],
        isPMB: true,
        pmbAuthNo: authNo,
        pmbStartDate: startDate,
        pmbEndDate: endDate,
      };
      return {
        ...prev,
        icds: newIcds,
      };
    });
  }, [pmbEditIndex]);

  const handleClosePMBModal = useCallback(() => {
    // If closing without saving and the ICD was just checked, uncheck it
    if (pmbEditIndex !== null && editDraft?.icds?.[pmbEditIndex]) {
      const icd = editDraft.icds[pmbEditIndex];
      if (icd.isPMB && !icd.pmbAuthNo) {
        handleICDUpdate(pmbEditIndex, {
          ...icd,
          isPMB: false,
        });
      }
    }
    setShowPMBModal(false);
    setPMBEditIndex(null);
  }, [pmbEditIndex, editDraft, handleICDUpdate]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await getPatientDiagnoses(patientId, practiceId);
        const norm = Array.isArray(res)
          ? res.map(normalize).sort(sortNewest)
          : [];
        if (!cancelled) {
          setList(norm);
          if (!norm.length) {
            setSelectedId(null);
            setSelected(null);
            setIsEditing(false);
          } else {
            const sid = norm[0]._id || `tmp-${0}`;
            setSelectedId(sid);
            setSelected(norm[0]);
            setIsEditing(false);
          }
        }
      } catch (e) {
        if (!cancelled) {
          setList([]);
          setSelectedId(null);
          setSelected(null);
          setErrorMsg("Failed to load diagnoses.");
        }
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [patientId, practiceId]);

  useEffect(() => {
    if (isEditing) return;

    if (!list.length) {
      setSelected(null);
      setSelectedId(null);
      return;
    }

    if (!selectedId) {
      setSelectedId(list[0]._id);
      setSelected(list[0]);
      return;
    }

    const found = list.find((x) => String(x._id) === String(selectedId));
    if (found) {
      setSelected(found);
    } else {
      setSelectedId(list[0]._id);
      setSelected(list[0]);
    }
  }, [list, selectedId, isEditing]);

  const hasICDs = (e) => Array.isArray(e?.icds) && e.icds.length > 0;

  const formatWhen = (iso) => {
    if (!iso) return "Unknown date";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "Unknown date";
    return d.toLocaleString([], {
      hour: "2-digit",
      minute: "2-digit",
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const MobileHeader = () => (
    <div
      className="bg-white"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 1030,
        borderBottom: "1px solid rgba(0,0,0,.06)",
      }}
    >
      <div className="d-flex align-items-start justify-content-between p-2 flex-wrap gap-2">
        <div className="me-2">
          <div className="fw-semibold text-primary" style={{ lineHeight: 1.2 }}>
            Diagnosis
          </div>
          <div className="text-muted small">
            {selected
              ? formatWhen(selected.date || selected.createdAt)
              : "No selection"}
          </div>
        </div>

        <div className="d-flex gap-2 flex-wrap">
          <Button
            size="sm"
            variant="primary"
            className="rounded-0"
            onClick={handleAdd}
          >
            + Add
          </Button>

          {selected && !isEditing && (
            <Button
              size="sm"
              variant="outline-secondary"
              className="rounded-0"
              onClick={startEdit}
            >
              Edit
            </Button>
          )}

          {selected && (
            <Button
              size="sm"
              variant="primary"
              className="rounded-0"
              onClick={() => setShowPicker(true)}
            >
              Next <i className="fa-solid fa-arrow-right"></i>
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  const handlePick = (id) => {
    setSelectedId(id);
    setShowPicker(false);
    setIsEditing(false);
    setErrorMsg("");
    setSuccessMsg("");
  };

  const handleAdd = () => {
    const draft = {
      _id: undefined,
      date: new Date().toISOString().slice(0, 10),
      note: "",
      icds: [],
    };
    setEditDraft(draft);
    setIsEditing(true);
    setSelectedId(NEW_ID);
    setSelected(draft);
    setErrorMsg("");
    setSuccessMsg("");
  };

  const startEdit = () => {
    if (!selected) return;
    setEditDraft(JSON.parse(JSON.stringify(selected)));
    setIsEditing(true);
    setErrorMsg("");
    setSuccessMsg("");
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditDraft(null);
    setErrorMsg("");
  };

  const closeICDPicker = () => setPickerOpenFor(null);
  
  const applyICDs = (selectedICDs) => {
    setEditDraft((prev) => {
      if (!prev) return prev;
      const prevICDs = Array.isArray(prev.icds) ? prev.icds : [];

      const selectedMap = new Map();
      for (const s of selectedICDs) {
        const key = codeKeyOf(s.item ?? s);
        selectedMap.set(key, s);
      }

      const mergedICDs = selectedICDs.map((s, i) => {
        const key = codeKeyOf(s.item ?? s);
        const existing = prevICDs.find((p) => codeKeyOf(p.item ?? p) === key);

        const uid = existing?.uid || `${codeKeyOf(s.item ?? s)}-${Date.now()}-${i}`;

        return {
          uid,
          item: s.item ?? s,
          isPrimary: !!s.isPrimary || !!existing?.isPrimary,
          useOnInvoice: s.useOnInvoice ?? existing?.useOnInvoice ?? false,
          isPMB: s.isPMB ?? existing?.isPMB ?? false,
          pmbAuthNo: s.pmbAuthNo ?? existing?.pmbAuthNo ?? "",
          pmbStartDate: s.pmbStartDate ?? existing?.pmbStartDate ?? "",
          pmbEndDate: s.pmbEndDate ?? existing?.pmbEndDate ?? "",
        };
      });

      return { ...prev, icds: mergedICDs };
    });
    setHasEditedICDs(true);
    closeICDPicker();
  };

  const handleDelete = async () => {
    if (!selected?._id) return;
    setSaving(true);
    setErrorMsg("");
    setSuccessMsg("");
    try {
      await deletePatientDiagnosis(selected._id);
      const fresh = (await getPatientDiagnoses(patientId, practiceId))
        .map(normalize)
        .sort(sortNewest);
      setList(fresh);
      setSuccessMsg("Diagnosis deleted.");
    } catch (e) {
      console.error(e);
      setErrorMsg("Failed to delete diagnosis.");
    } finally {
      setSaving(false);
      setIsEditing(false);
    }
  };

  const isChanged = useMemo(() => {
    if (!isEditing) return false;
    if (!editDraft) return false;
    const a = selected?._id ? selected : { ...selected, _id: undefined };
    const A = {
      date: a?.date || "",
      note: a?.note || "",
      icds: (a?.icds || [])
        .map((s) => ({ code: codeKeyOf(s.item ?? s) || "", p: !!s.isPrimary }))
        .sort((x, y) => x.code.localeCompare(y.code)),
    };
    const B = {
      date: editDraft.date || "",
      note: editDraft.note || "",
      icds: (editDraft.icds || [])
        .map((s) => ({ code: codeKeyOf(s.item ?? s) || "", p: !!s.isPrimary }))
        .sort((x, y) => x.code.localeCompare(y.code)),
    };
    return JSON.stringify(A) !== JSON.stringify(B);
  }, [isEditing, selected, editDraft]);

  const saveOne = async () => {
    if (!isEditing || saving || !editDraft) return;
    if (!Array.isArray(editDraft.icds) || editDraft.icds.length === 0) {
      setErrorMsg("Please select at least one ICD-10 code.");
      return;
    }
    setSaving(true);
    setErrorMsg("");
    setSuccessMsg("");
    try {
      if (selected?._id) {
        await updatePatientDiagnosis(selected._id, {
          date: editDraft.date,
          note: editDraft.note,
          icds: editDraft.icds,
        });
      } else {
        const created = await createPatientDiagnosis(patientId, {
          date: editDraft.date,
          note: editDraft.note,
          icds: editDraft.icds,
          practiceId,
        });
        setSelectedId(created?._id);
      }
      const fresh = (await getPatientDiagnoses(patientId, practiceId))
        .map(normalize)
        .sort(sortNewest);
      setList(fresh);
      const now =
        fresh.find((x) => String(x._id) === String(selected?._id)) ||
        fresh.find((x) => String(x._id) === String(selectedId)) ||
        fresh[0];
      setSelected(now || null);
      if (now?._id) setSelectedId(now._id);
      setIsEditing(false);
      setIsEditingICDs(false);
      setEditDraft(null);
      setSuccessMsg("Diagnosis saved.");
    } catch (e) {
      console.error(e);
      setErrorMsg("Failed to save diagnosis.");
    } finally {
      setSaving(false);
    }
  };

  // A one-cell-per-row table was a card list in table markup. `.acc-list`
  // supplies the surface `.acc-card` is scoped to, and the selected row uses
  // the brand tint rather than a solid fill — same call as SessionNoteRail,
  // since this is a picker column beside the diagnosis detail.
  const DesktopList = () => (
    <div className="acc-list">
      {list.length ? (
        list.map((d, i) => {
          const isActive = String(selectedId) === String(d._id);
          const codes = (d.icds || [])
            .map((s) => codeKeyOf(s.item ?? s))
            .filter(Boolean);
          const preview = codes.slice(0, 3).join(", ");
          const extra = codes.length > 3 ? ` +${codes.length - 3}` : "";

          return (
            <div
              key={d._id || `row-${i}`}
              className="acc-card small"
              style={{
                cursor: "pointer",
                backgroundColor: isActive ? "var(--acc-brand-tint)" : "#fff",
                borderColor: isActive ? "var(--acc-brand-primary)" : "var(--acc-border-color)",
              }}
              onClick={() => handlePick(d._id)}
            >
              <div className="d-flex justify-content-between align-items-center">
                <div className="fw-semibold text-primary">
                  {d.date || "—"}
                </div>
                {codes.length > 0 && <Badge bg="primary">ICD</Badge>}
              </div>
              <div
                className="text-muted small text-truncate"
                title={codes.join(", ")}
              >
                {codes.length ? `${preview}${extra}` : "—"}
              </div>
            </div>
          );
        })
      ) : (
        <p className="text-muted small mb-0">No diagnoses available.</p>
      )}
    </div>
  );

  const PickerSheet = () => (
    <Offcanvas
      show={showPicker}
      onHide={() => setShowPicker(false)}
      placement="bottom"
      backdrop
      scroll
      className="sessions-drawer"
      style={{ height: "50vh" }}
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title className="text-primary">
          Choose a Diagnosis
        </Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body>
        <ListGroup variant="flush">
          {list.length ? (
            list.map((d) => {
              const isActive = String(selectedId) === String(d._id);
              const codes = (d.icds || [])
                .map((s) => codeKeyOf(s.item ?? s))
                .filter(Boolean);
              const preview = codes.slice(0, 3).join(", ");
              const extra = codes.length > 3 ? ` +${codes.length - 3}` : "";

              return (
                <ListGroup.Item
                  key={d._id}
                  action
                  onClick={() => handlePick(d._id)}
                  className={`d-flex justify-content-between align-items-center ${
                    isActive ? "bg-primary bg-opacity-10" : ""
                  }`}
                >
                  <div className="me-2">
                    <div className="fw-semibold text-primary">
                      {d.date || "—"}
                    </div>
                    <div
                      className="text-muted small text-truncate"
                      title={codes.join(", ")}
                    >
                      {codes.length ? `${preview}${extra}` : "—"}
                    </div>
                  </div>
                  {codes.length > 0 && <Badge bg="primary">ICD</Badge>}
                </ListGroup.Item>
              );
            })
          ) : (
            <div className="text-muted small">No diagnoses available.</div>
          )}
        </ListGroup>
      </Offcanvas.Body>
    </Offcanvas>
  );

  const DetailCard = () => {
    if (!selected && !isEditing) {
      return <p className="text-muted m-2">No diagnosis selected.</p>;
    }

    const view = isEditing ? editDraft : selected;

    if (isEditing) {
      if (!editDraft) return null;

      return (
        <Card className="shadow-sm">
          <Card.Body>
            {errorMsg && <Alert variant="warning">{errorMsg}</Alert>}
            {successMsg && <Alert variant="success">{successMsg}</Alert>}

            <Row className="g-2 align-items-start">
              <Col md={3} sm={6} xs={12}>
                <Form.Label className="text-primary fw-semibold">Date</Form.Label>
                <Form.Control
                  type="date"
                  value={editDraft?.date || ""}
                  onChange={(e) =>
                    setEditDraft((p) => ({
                      ...(p || {}),
                      date: e.target.value,
                    }))
                  }
                />
              </Col>

              <Col md={9} sm={12} xs={12}>
                <Form.Label className="text-primary fw-semibold">Note</Form.Label>
                <Form.Control
                  type="text"
                  value={editDraft?.note || ""}
                  onChange={(e) =>
                    setEditDraft((p) => ({
                      ...(p || {}),
                      note: e.target.value,
                    }))
                  }
                  autoFocus={!isEditingICDs}
                />
              </Col>
            </Row>

            <div className="mt-3">
              <div className="d-flex justify-content-between align-items-center">
                <Form.Label className="text-primary fw-semibold mb-0">ICD-10</Form.Label>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setPickerOpenFor(1)}
                  className="rounded-0"
                >
                  <i className="fa-solid fa-list-check me-2" />
                  {hasICDs(editDraft) ? "Edit Codes" : "Add Codes"}
                </Button>
              </div>

              {!hasICDs(editDraft) ? (
                <div className="text-muted mt-2">No codes selected.</div>
              ) : (
                <div className="d-flex flex-column gap-2 mt-2">
                  {editDraft.icds.map((s, i) => {
                    const stableKey = s.uid || `icd-${i}`;
                    const label = icdLabel(s.item);
                    return (
                      <ICDEditRow
                        key={stableKey}
                        icd={s}
                        index={i}
                        stableKey={stableKey}
                        label={label}
                        onUpdate={handleICDUpdate}
                        onEditPMB={handleEditPMB}
                      />
                    );
                  })}
                </div>
              )}
            </div>

            <div className="d-flex justify-content-end gap-2 mt-3">
              {selected?._id && (
                <Button
                  variant=""
                  onClick={handleDelete}
                  disabled={saving}
                  title="Delete"
                >
                  <i className="fa-solid fa-trash-can text-danger" />
                </Button>
              )}
              <Button variant="outline-secondary" onClick={cancelEdit} disabled={saving}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={saveOne}
                disabled={saving}
                aria-live="polite"
              >
                {saving && (
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    className="me-2"
                    aria-hidden="true"
                  />
                )}
                {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </Card.Body>
        </Card>
      );
    }

    // READ-ONLY VIEW
    return (
      <Card className="shadow-sm">
        <Card.Body>
          {errorMsg && <Alert variant="warning">{errorMsg}</Alert>}
          {successMsg && <Alert variant="success">{successMsg}</Alert>}

          <Row className="g-2">
            <Col md={3} sm={6} xs={12}>
              <div className="fw-semibold text-primary">Date</div>
              <div>{view?.date || "—"}</div>
            </Col>
            <Col md={9} sm={12}>
              <div className="fw-semibold text-primary">Note</div>
              <div>{view?.note || <span className="text-muted">—</span>}</div>
            </Col>
          </Row>

          <div className="mt-3">
            <div className="fw-semibold text-primary mb-1">ICD-10</div>
            {!hasICDs(view) ? (
              <div className="text-muted">None</div>
            ) : (
              <div className="d-flex flex-column gap-2">
                {view.icds.map((s, i) => {
                  const key = codeKeyOf(s.item ?? s) || `${i}`;
                  const label = icdLabel(s.item);
                  return (
                    <div
                      className="d-flex align-items-center justify-content-between border rounded p-2"
                      key={key}
                    >
                      <div className="min-w-0 me-2 flex-grow-1">
                        <div className="icd-line">{label || "(ICD code)"}</div>
                      </div>
<div className="d-flex flex-column flex-shrink-0">
  <div className="d-flex align-items-center gap-2">
    {s.isPrimary && <Badge bg="primary">Primary</Badge>}
    {s.useOnInvoice && (
      <Badge bg="info">
        <i className="fa-solid fa-file-invoice me-1" />
        Invoice
      </Badge>
    )}
  </div>

  {s.isPMB && (
    <div className="mt-1">
      <Badge bg="success">
        <i className="fa-solid fa-heart-pulse me-1" />
        PMB: {s.pmbAuthNo}
        {s.pmbStartDate && ` (${s.pmbStartDate} to ${s.pmbEndDate || "…"})`}
      </Badge>
    </div>
  )}
</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="d-none d-md-flex justify-content-end gap-2 mt-3">
            <Button variant="outline-primary" onClick={startEdit}>
              <i className="fa-regular fa-pen-to-square me-2" />
              Edit
            </Button>
          </div>
        </Card.Body>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="d-flex align-items-center gap-2">
        <Spinner animation="border" size="sm" /> <span>Loading diagnoses…</span>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className="pb-3">
        <MobileHeader />
        <div className="px-2">
          {!selected && (
            <Card className="shadow-sm">
              <Card.Body className="text-muted">
                No diagnoses yet. Tap "+ Add".
              </Card.Body>
            </Card>
          )}
          {selected && (
            <>
              <DetailCard />
            </>
          )}
        </div>
        <PickerSheet />
        {isEditing && (
          <>
            <ICD10PickerModal
              show={pickerOpenFor != null}
              onHide={closeICDPicker}
              onConfirm={applyICDs}
              initialSelected={editDraft?.icds || []}
            />
            <PMBAuthModal
              show={showPMBModal}
              onHide={handleClosePMBModal}
              onSave={handleSavePMB}
              authNo={pmbEditIndex !== null ? editDraft?.icds?.[pmbEditIndex]?.pmbAuthNo : ""}
              startDate={pmbEditIndex !== null ? editDraft?.icds?.[pmbEditIndex]?.pmbStartDate : ""}
              endDate={pmbEditIndex !== null ? editDraft?.icds?.[pmbEditIndex]?.pmbEndDate : ""}
            />
          </>
        )}
      </div>
    );
  }

  // Desktop 2-column layout
  return (
    <div>
      <Row className="align-items-center ms-2">
        <Col md={3} className="d-flex flex-column">
          <h6 className="text-primary mb-0"></h6>
        </Col>
        <Col md={9} className="d-flex justify-content-end">
          {!isEditing && (
            <Button variant="primary" onClick={handleAdd}>
              + Add Diagnosis
            </Button>
          )}
        </Col>
      </Row>

      <Row>
        <Col md={3} className="d-flex justify-content-start">
          <p className="text-muted small mb-0 ms-4">
            {list.length > 0
              ? `${list.length} record(s)`
              : "No diagnoses available"}
          </p>
        </Col>
      </Row>

      {list.length > 0 || isEditing ? (
        <Row className="g-3 mb-2">
          <Col md={3} className="left-col position-sticky" style={{ top: 82 }}>
            <DesktopList />
          </Col>
          <Col md={9} className="right-col position-relative">
            <div className="right-scroll">
              <DetailCard />
            </div>
          </Col>
        </Row>
      ) : (
        <Row className="text-center mt-3">
          <Col>
            <p className="text-muted">No diagnoses yet. Click "+ Add".</p>
          </Col>
        </Row>
      )}

      {isEditing && (
        <>
          <ICD10PickerModal
            show={pickerOpenFor != null}
            onHide={closeICDPicker}
            onConfirm={applyICDs}
            initialSelected={editDraft?.icds || []}
          />
          <PMBAuthModal
            show={showPMBModal}
            onHide={handleClosePMBModal}
            onSave={handleSavePMB}
            authNo={pmbEditIndex !== null ? editDraft?.icds?.[pmbEditIndex]?.pmbAuthNo : ""}
            startDate={pmbEditIndex !== null ? editDraft?.icds?.[pmbEditIndex]?.pmbStartDate : ""}
            endDate={pmbEditIndex !== null ? editDraft?.icds?.[pmbEditIndex]?.pmbEndDate : ""}
          />
        </>
      )}
    </div>
  );
}